import { createElement, type ReactElement } from "react";
import { utcIsoToEpochMs } from "@zzk/core";

import type { CachedOverview } from "../../../integration/zzk-sec.inbound.js";

type KpiTone = "neutral" | "success" | "warning" | "danger" | "info";

const KPI_LABELS: Record<keyof CachedOverview["data"], string> = {
  tenants: "Tenants",
  devicesTotal: "Devices (total)",
  devicesActive: "Devices ativos",
  devicesRevoked: "Devices revogados",
  instancesActive24h: "Instâncias 24h",
  auditEvents24h: "Audit 24h",
  enrollTokensPending: "Enroll pendentes",
  failures24h: "Falhas 24h",
};

const KPI_ORDER: readonly (keyof CachedOverview["data"])[] = [
  "tenants",
  "devicesTotal",
  "devicesActive",
  "devicesRevoked",
  "instancesActive24h",
  "auditEvents24h",
  "enrollTokensPending",
  "failures24h",
];

export interface OverviewCardProps {
  readonly overview: CachedOverview | null;
  readonly error?: string | null;
  readonly loading?: boolean;
}

export function OverviewCard(props: OverviewCardProps): ReactElement {
  if (props.loading) return renderLoading();
  if (props.error) return renderError(props.error);
  if (!props.overview) return renderEmpty();
  return renderOverview(props.overview);
}

function renderOverview(overview: CachedOverview): ReactElement {
  const healthLabel =
    overview.kind === "fresh" ? "Snapshot atualizado" : "Snapshot em cache";

  return createElement(
    "section",
    {
      className: "zzk-sec-overview-card",
      "data-testid": "overview-card",
      "data-kind": overview.kind,
      "aria-busy": "false",
    },
    [
      createElement(
        "header",
        {
          className: "zzk-sec-overview-header",
          key: "header",
          "data-testid": "overview-freshness",
          "data-fetched-at": overview.fetched_at,
        },
        [
          createElement(
            "div",
            { key: "title", className: "zzk-sec-overview-title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Snapshot executivo",
              ),
              createElement(
                "strong",
                { key: "heading", className: "zzk-sec-overview-heading" },
                healthLabel,
              ),
            ],
          ),
          createElement(
            "div",
            {
              key: "meta",
              className: "zzk-sec-overview-meta",
            },
            [
              createElement(
                "span",
                {
                  key: "kind",
                  className: "zzk-sec-status-pill",
                  "data-tone": overview.kind === "fresh" ? "success" : "warning",
                  "data-testid": "overview-kind",
                },
                overview.kind,
              ),
              createElement(
                "time",
                {
                  key: "time",
                  dateTime: overview.fetched_at,
                  "data-testid": "overview-fetched-at",
                },
                `Atualizado ${formatPtBrDateTime(overview.fetched_at)}`,
              ),
              createElement(
                "span",
                { key: "ttl", className: "zzk-sec-muted" },
                `TTL ${overview.ttl_seconds}s`,
              ),
            ],
          ),
        ],
      ),
      createElement(
        "dl",
        {
          key: "grid",
          className: "zzk-sec-kpi-grid",
          "data-testid": "overview-kpi-grid",
        },
        KPI_ORDER.map((field) => {
          const item = createKpiView(field, overview);
          return createElement(
            "div",
            {
              key: field,
              className: "zzk-sec-kpi",
              "data-tone": item.tone,
              "data-testid": "kpi-card",
              "data-kpi-field": field,
            },
            [
              createElement(
                "dt",
                { key: "label", "data-testid": "kpi-label" },
                item.label,
              ),
              createElement(
                "dd",
                { key: "value", "data-testid": "kpi-value" },
                item.value,
              ),
              createElement(
                "span",
                { key: "hint", className: "zzk-sec-kpi-hint" },
                item.hint,
              ),
            ],
          );
        }),
      ),
    ],
  );
}

function createKpiView(
  field: keyof CachedOverview["data"],
  overview: CachedOverview,
): {
  readonly label: string;
  readonly value: string;
  readonly hint: string;
  readonly tone: KpiTone;
} {
  const data = overview.data;
  const value = data[field];
  const activeRate = formatPercent(data.devicesActive, data.devicesTotal);
  const revokedRate = formatPercent(data.devicesRevoked, data.devicesTotal);

  if (field === "devicesActive") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: `${activeRate} da base total`,
      tone: "success",
    };
  }

  if (field === "devicesRevoked") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: `${revokedRate} da base total`,
      tone: value > 0 ? "warning" : "success",
    };
  }

  if (field === "failures24h") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: value > 0 ? "Falhas nas últimas 24h" : "Sem falhas nas últimas 24h",
      tone: value > 0 ? "danger" : "success",
    };
  }

  if (field === "enrollTokensPending") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: value > 0 ? "Aguardando ativação" : "Fila limpa",
      tone: value > 0 ? "warning" : "success",
    };
  }

  if (field === "auditEvents24h") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: "Eventos auditáveis em 24h",
      tone: "info",
    };
  }

  if (field === "instancesActive24h") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: "Instâncias vistas na janela",
      tone: "info",
    };
  }

  if (field === "devicesTotal") {
    return {
      label: KPI_LABELS[field],
      value: formatNumber(value),
      hint: `${formatNumber(data.devicesActive)} ativos`,
      tone: "neutral",
    };
  }

  return {
    label: KPI_LABELS[field],
    value: formatNumber(value),
    hint: "Tenants monitorados",
    tone: "neutral",
  };
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("pt-BR").format(value);
}

function formatPercent(numerator: number, denominator: number): string {
  if (denominator <= 0) return "0%";
  return `${Math.round((numerator / denominator) * 100)}%`;
}

function formatPtBrDateTime(value: string): string {
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "medium",
    timeZone: "America/Sao_Paulo",
  }).format(utcIsoToEpochMs(value));
}

function renderLoading(): ReactElement {
  return createElement(
    "section",
    {
      className: "zzk-sec-overview-card",
      "data-testid": "overview-loading",
      "aria-busy": "true",
    },
    [
      createElement(
        "header",
        { key: "header", className: "zzk-sec-overview-header" },
        [
          createElement(
            "div",
            { key: "title", className: "zzk-sec-overview-title" },
            [
              createElement(
                "span",
                { key: "eyebrow", className: "zzk-sec-eyebrow" },
                "Snapshot executivo",
              ),
              createElement(
                "strong",
                { key: "heading", className: "zzk-sec-overview-heading" },
                "Carregando KPIs operacionais",
              ),
            ],
          ),
        ],
      ),
      createElement(
        "div",
        { key: "grid", className: "zzk-sec-kpi-grid" },
        Array.from({ length: 8 }, (_, index) =>
          createElement(
            "div",
            {
              key: index,
              className: "zzk-sec-kpi zzk-sec-kpi--loading",
            },
            [
              createElement(
                "span",
                { key: "label", className: "zzk-sec-loading-line" },
              ),
              createElement(
                "span",
                { key: "value", className: "zzk-sec-loading-line" },
              ),
            ],
          ),
        ),
      ),
    ],
  );
}

function renderEmpty(): ReactElement {
  return createElement(
    "section",
    { className: "zzk-sec-feedback-card", "data-testid": "overview-empty" },
    "Sem dados ainda.",
  );
}

function renderError(message: string): ReactElement {
  return createElement(
    "section",
    {
      className: "zzk-sec-feedback-card",
      "data-tone": "danger",
      "data-testid": "overview-error",
      role: "alert",
    },
    message,
  );
}
